import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import axios from "axios";
import SeoMeta from "../components/SeoMeta";
import { PageHero } from "../components/site/Sections";
import { MethodSection, ScienceSection, GallerySection } from "../components/site/EventsSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const firstReleaseText = (value) =>
  typeof value === "string"
    ? value.replace(/pre[- ]?release/gi, (match) =>
        match.includes("Release") ? "1st Release" : "1st release"
      )
    : value;

export const springText = (ev, value) =>
  ev?.slug === "spring-feral-fest" ? firstReleaseText(value) : value;

const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n) || 0);

// For the retreat, derive the price label from the live room prices so it never goes stale.
export const priceOf = (ev) => {
  if (ev?.slug === "clarity-reset") {
    const acc = ev.accommodation || [];
    const solo = acc.find((a) => a.id === "clarity-solo")?.price;
    const share = acc.find((a) => a.perPerson);
    const pp = share ? Math.round(share.price / 2) : null;
    if (solo) return `From ${gbp(solo)} solo${pp ? ` · ${gbp(pp)}pp sharing` : ""}`;
  }
  return springText(ev, ev.priceFrom);
};

const EventsList = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const cardImage = (ev) => (ev.images || [])[0] || ev.cardImage;
  const tagOf = (ev) => springText(ev, ev.tag || (ev.type === "festival" ? "Festival" : "Event"));
  const categoryOf = (ev) => ev.category || ev.subtitle || "";

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <SeoMeta title="Wellness retreats and events in Anglesey | FERAL Collective" description="Gather. Reset. Rise. Retreats, gatherings and festivals rooted in community, wellness, nature and food, hosted in our own estates on Anglesey and beyond." canonicalPath="/events" image="/home/candle-dinner.webp" />
      <Navbar />

      <PageHero testid="events-hero" eyebrow="Events & Retreats" title="Gather. Reset. Rise." image="/home/fire-feast.webp"
        copy="Retreats, gatherings and festivals rooted in community, wellness, nature and food. Hosted on our own estates on Anglesey and beyond." />

      {/* Event list */}
      <section className="feral-texture py-20 px-6" data-testid="events-upcoming">
        <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl text-center mb-12">Upcoming events</h2>
        <div className="max-w-5xl mx-auto space-y-10">
          {events.map((ev) => (
            <Link
              key={ev.slug}
              to={`/events/${ev.slug}`}
              data-testid={`event-card-${ev.slug}`}
              className="group grid grid-cols-1 md:grid-cols-2 gap-0 bg-white rounded-md overflow-hidden shadow-lg border border-[#d9c3a8] hover:shadow-2xl transition-shadow duration-300"
            >
              <div className="relative h-72 md:h-auto overflow-hidden">
                <img
                  src={cardImage(ev)}
                  alt={ev.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-white/90 text-[#9b3d0a] text-xs font-semibold tracking-wide px-3 py-1">
                  {ev.invitationOnly ? "INVITATION ONLY" : tagOf(ev)}
                </span>
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <p className="text-[#9b3d0a] tracking-[0.15em] text-xs font-semibold uppercase mb-3">
                  {categoryOf(ev)}
                </p>
                <h2 className="feral-serif text-[#9b3d0a] text-4xl mb-4">
                  {ev.name}
                </h2>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-[#6e5646] text-sm">
                    <Calendar size={16} className="text-[#9b3d0a]" />
                    <span>{ev.dateLine}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#6e5646] text-sm">
                    <MapPin size={16} className="text-[#9b3d0a]" />
                    <span>{ev.venue}</span>
                  </div>
                </div>
                {ev.invitationOnly ? (
                  <>
                    {ev.subtitle && <p className="text-[#6e5646] text-sm mb-6 italic">{ev.subtitle}</p>}
                    <span className="inline-flex items-center gap-2 text-[#9b3d0a] font-semibold tracking-[0.1em] text-sm group-hover:gap-3 transition-all">
                      VIEW EVENT <ArrowRight size={18} />
                    </span>
                  </>
                ) : (
                  <>
                    <p className="text-[#9b3d0a] font-semibold mb-6">{priceOf(ev)}</p>
                    <span className="inline-flex items-center gap-2 text-[#9b3d0a] font-semibold tracking-[0.1em] text-sm group-hover:gap-3 transition-all">
                      BOOK NOW <ArrowRight size={18} />
                    </span>
                  </>
                )}
              </div>
            </Link>
          ))}

          <p className="text-center text-[#9a876f] tracking-[0.2em] text-sm font-semibold pt-4">
            MORE EVENTS COMING SOON
          </p>
        </div>
      </section>

      <MethodSection />
      <ScienceSection />
      <GallerySection />
      <Footer />
    </div>
  );
};

export default EventsList;
